import React, { useState, useEffect, useRef } from 'react';
import { ref, get, update, push, serverTimestamp, runTransaction, onValue } from 'firebase/database';
import { database, auth } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { updatePurchasedCredits } from './Credits';
import { recordPurchaseBillings } from './Billings';
import { stripePublishKey } from '../config'; // Import the Stripe publish key


let stripePromise;

if (stripePublishKey) {
  stripePromise = loadStripe(stripePublishKey);
} else {
  stripePromise = Promise.resolve(null);
}

stripePromise.catch(error => {
  //console.error('Error initializing Stripe:', error);
});

const DashboardPage = () => {
  
  const { currentUser } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [credits, setCredits] = useState({ purchased: 0, used: 0, balance: 0 });
  const [loading, setLoading] = useState(true);
  const [showPurchase, setShowPurchase] = useState(false);
  const [dashboard, setDashboard] = useState(true);
  const [smsHistory, setSMSHistory] = useState(false);
  const [billingHistory, setBillingHistory] = useState(false);
  const [redeemCode, setRedeemCode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [purchasedCreditsAmount, setPurchasedCreditsAmount] = useState(0);
  const [isMounted, setIsMounted] = useState(true);
  const [billingRecords, setBillingRecords] = useState([]);
  const [smsRecords, setSMSRecords] = useState([]);
  const [smsReceivedRecords, setSMSReceivedRecords] = useState([]);
  const [showApiKey, setShowApiKey] = useState(false); // Add this line
  const hasProcessedPurchaseRef = useRef(false); // Use a ref to track purchase processing
  const [fullMessage, setFullMessage] = useState(''); // State to hold the full message
  const [isMessagePopupVisible, setMessagePopupVisible] = useState(false); // State to control popup visibility
  const [currentPage, setCurrentPage] = useState(0);
  const recordsPerPage = 10;
  const [redeemCodeValue, setRedeemCodeValue] = useState(''); // Add state for input value
  const [error, setError] = useState(''); // Add state for error message
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const [webhookUrl, setWebhookUrl] = useState('https://default-webhook-url.com'); // Add state for webhook URL
  const [activeTab, setActiveTab] = useState('sent'); // Add state for active tab
  

  useEffect(() => {
    if (currentUser) {
      const userRef = ref(database, 'Users/' + currentUser.uid);
      const creditsRef = ref(database, 'Credits/' + currentUser.uid);
      //console.log(currentUser.uid)
      Promise.all([
        get(userRef),
        get(creditsRef)
      ]).then(([userSnapshot, creditsSnapshot]) => {
        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          setApiKey(userData.APIKey || '');
          // Fetch the WebhookUrl from user data
          setWebhookUrl(userData.WebhookUrl || 'https://default-webhook-url.com'); // Set the fetched WebhookUrl or default
        }
        if (creditsSnapshot.exists()) {
          const creditsData = creditsSnapshot.val();
          const newCredits = {
            purchased: creditsData.totalCreditsPurchased,
            used: creditsData.totalCreditsUsed,
            balance: creditsData.totalCreditBalance       
          };
          setCredits(newCredits); // Update credits state
          
          // Check for transaction status after credits are set
          const params = new URLSearchParams(location.search);
          const transaction = params.get('transaction');
          const purchasedCredits = parseInt(params.get('credits'), 0);
          
          if (transaction === 'success' && !isNaN(purchasedCredits) && !hasProcessedPurchaseRef.current) { // Check if purchase has been processed
            hasProcessedPurchaseRef.current = true; // Set to true to prevent re-processing
            setTransactionStatus('success');
            setPurchasedCreditsAmount(purchasedCredits);
            // Call handleUpdateCredits with the updated credits
            handleUpdateCredits(purchasedCredits, newCredits); // Use newCredits here
            // Call handleInsertBilling with the purchased credits
            handleInsertBilling(calculateAmountPaid(purchasedCredits), purchasedCredits)
            navigate('/dashboard', { replace: true });
          } else if (transaction === 'failed') {
            console.log('Setting transaction status to failed');
            setTransactionStatus('failed');
            // Clean the URL
            navigate('/dashboard', { replace: true });
          }
        }
        setLoading(false);
      }).catch((error) => {
        //console.error("Error fetching data:", error);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [location.search, currentUser]); // Add hasProcessedPurchase to dependencies

  //useEffect to Fetch Billing Records for the billing History section
  useEffect(() => {
    if (currentUser) {
      const billingRecordsRef = ref(database, 'Billing/' + currentUser.uid); // Reference to the user's billing records
      onValue(billingRecordsRef, (snapshot) => {
        if (snapshot.exists()) {
          const recordsData = snapshot.val();
          const recordsArray = Object.keys(recordsData).map(key => ({
            id: key,
            ...recordsData[key],
          })).sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort in descending order by date
          setBillingRecords(recordsArray); // Set the billing records in state
        } else {
          setBillingRecords([]); // Set to empty array if no records exist
        }
      }, (error) => {
        //console.error("Error fetching billing records:", error);
      });
    } else {
      setBillingRecords([]); // Clear records if no user is logged in
    }
  }, [currentUser]); // Dependency on currentUser

//useEffect to Fetch SMS Records for the SMS History section
useEffect(() => {
  if (currentUser) {
    const smsRecordsRef = ref(database, 'SMSHistory/' + currentUser.uid); // Reference to the user's billing records
    onValue(smsRecordsRef, (snapshot) => {
      if (snapshot.exists()) {
        const recordsData = snapshot.val();
        const recordsArray = Object.keys(recordsData).map(key => ({
          id: key,
          ...recordsData[key],
        })).sort((a, b) => new Date(b.DateTime) - new Date(a.DateTime)); // Sort in descending order by date
        console.log('SMS Records Array:', recordsArray); // Log the processed records array
        setSMSRecords(recordsArray); // Set the SMS records in state
      } else {
        setSMSRecords([]); // Set to empty array if no records exist
      }
    }, (error) => {
      //console.error("Error fetching SMS records:", error);
    });
  } else {
    setSMSRecords([]); // Clear records if no user is logged in
  }
}, [currentUser]); // Dependency on currentUser

//useEffect to Fetch SMS Records for the SMS Received section
useEffect(() => {
  if (currentUser) {
    const smsReceivedRecordsRef = ref(database, 'SMSReceived/' + currentUser.uid); // Reference to the user's billing records
    onValue(smsReceivedRecordsRef, (snapshot) => {
      if (snapshot.exists()) {
        const recordsData = snapshot.val();
        const recordsArray = Object.keys(recordsData).map(key => ({
          id: key,
          ...recordsData[key],
        })).sort((a, b) => new Date(b.DateTime) - new Date(a.DateTime)); // Sort in descending order by date
        console.log('SMS received Records Array:', recordsArray); // Log the processed records array
        setSMSReceivedRecords(recordsArray); // Set the SMS records in state
      } else {
        setSMSReceivedRecords([]); // Set to empty array if no records exist
      }
    }, (error) => {
      //console.error("Error fetching SMS records:", error);
    });
  } else {
    setSMSReceivedRecords([]); // Clear records if no user is logged in
  }
}, [currentUser]); // Dependency on currentUser


  const handleInsertBilling = async (amountPaid, newCredits) => { // Accept currentCredits as a parameter
    try {
      const creditId = currentUser.uid;
      // Update the parameters to match the updateCredits function
      const result = await recordPurchaseBillings(creditId, 
        amountPaid, 
        newCredits,
        'Stripe Payment'
      );
      if (result) {
        console.log('Billing information inserted successfully in the database');
      }
    } catch (error) {
      //console.error('Error inserting Billing information:', error);
    }
  };


 const handleUpdateCredits = async (newCredits, currentCredits) => { // Accept currentCredits as a parameter
    try {
      const creditId = currentUser.uid;
      // Update the parameters to match the updateCredits function
      const result = await updatePurchasedCredits(creditId, 
        currentCredits.purchased + newCredits, // creditsPurchased
        currentCredits.used,                   // creditsUsed
        currentCredits.balance + newCredits     // creditBalance
      );
      if (result) {
        console.log('Credits updated successfully in the database');
      }
    } catch (error) {
      //console.error('Error updating credits:', error);
    }
  };

  const handleRedeem = async (code) => {
    setIsLoading(true); // Set loading state to true
    if (!code) {
      setError('Please enter a code to redeem.');
      setIsLoading(false); // Reset loading state after processing
      return;
    } else {
      setError(''); // Clear the error message when a valid code is entered
      // Call the redeem code cloud function
      try {
        
        const response = await fetch('https://us-central1-smstextr.cloudfunctions.net/redeemcode', {//http://localhost:5001/smstextr/us-central1/redeemcode
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uid: currentUser.uid, // Current user's UID
            code: code, // The redeem code
          }),
        });


        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message);
        }
       
        setError(data.message);

      } catch (error) {
        console.error('Error redeeming code:', error.message);
        setError(error.message); // Set error message if the request fails
      }
    }
    setIsLoading(false); // Reset loading state after processing
  };

  const handlePurchase = async (priceId, credits) => {
    try {
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to initialize. Please check your configuration.');
      }
      const response = await fetch('https://us-central1-smstextr.cloudfunctions.net/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: priceId,
          userId: currentUser.uid,
          credits: credits,
        }),
      });

      console.log('Response status:', response.status);
      const responseData = await response.json();
      console.log('Response data:', responseData);

      if (!response.ok) {
        throw new Error(`Failed to create checkout session: ${JSON.stringify(responseData)}`);
      }

      const { sessionId } = responseData;

      //console.log('Redirecting to checkout with sessionId:', sessionId);
      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        //console.error("Stripe checkout error:", error);
        if (isMounted) {
          alert(`Stripe checkout error: ${error.message}`);
        }
      } else {
        console.log('Stripe checkout completed');
        if (isMounted) {
          //setTransactionStatus('success');
          //setPurchasedCreditsAmount(credits);
        }
      }
    } catch (error) {
      //console.error("Error in handlePurchase:", error);
      if (isMounted) {
        setTransactionStatus('failed');
        alert(`An error occurred: ${error.message}. Please try again or contact support.`);
      }
    }
  };


  const toggleApiKeyVisibility = () => {
    setShowApiKey(prev => !prev);
  };

  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey).then(() => {
      alert('API Key copied to clipboard!');
    }).catch(err => {
      //console.error('Failed to copy API Key: ', err);
    });
  };

  // Helper function to calculate amount paid based on credits purchased
  const calculateAmountPaid = (credits) => {
    const prices = {
      50: 3,
      250: 10,
      1000: 30,
      2500: 50
    };
    return prices[credits] || 0;
  };

   const handleBillingHistory = () => {
    setBillingHistory(true);
    setSMSHistory(false);
    setShowPurchase(false);
    setDashboard(false);
    setRedeemCode(false);
  };

  const handleLogout = () => {
    auth.signOut().then(() => {
      navigate('/');
    }).catch((error) => {
      //console.error("Error signing out: ", error);
    });
  };

  const handlePurchaseCredit = () => {
    setShowPurchase(true);
    setSMSHistory(false);
    setBillingHistory(false);
    setDashboard(false);
    setRedeemCode(false);
  };

  const handleDashboard = () => {
    setDashboard(true);
    setShowPurchase(false);
    setSMSHistory(false);
    setBillingHistory(false);
    setRedeemCode(false);
  };

  const handleSMSHistory = () => {
    setSMSHistory(true);
    setBillingHistory(false);
    setShowPurchase(false);
    setDashboard(false);
    setRedeemCode(false);
  };

  const handleRedeemCode = () => {
    setRedeemCode(true);
    setSMSHistory(false);
    setBillingHistory(false);
    setShowPurchase(false);
    setDashboard(false);
  };

  const handleClosePopup = () => {
    console.log('Closing popup');
    setTransactionStatus(null);
    // Ensure the URL is clean when closing the popup
    navigate('/dashboard', { replace: true });
  };


  const iconButtonStyle = {
    marginLeft: '10px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#4CAF50',
  };

  // Update the function to show the full message
  const handleShowFullMessage = (message) => {
    setFullMessage(message);
    setMessagePopupVisible(true);
  };

  // Function to close the popup
  const handleCloseMessagePopup = () => {
    setMessagePopupVisible(false);
  };

  const handlePreviousPage = () => {
    setCurrentPage(prevPage => prevPage - 1);
  };

  const handleNextPage = () => {
    setCurrentPage(prevPage => prevPage + 1);
  };

    if (loading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <div>Please log in to view the dashboard.</div>;
  }

  return (
    <div style={dashboardContainerStyle}>
      <div style={sidebarStyle}>
        <img src="/SMS_Texter_Logo.png" alt="SMS Texter Logo" style={logoStyle} />
        <button onClick={handleDashboard} style={dashboardButtonStyle}>Home</button>
        <button onClick={handleBillingHistory} style={dashboardButtonStyle}>Billing History</button>
        <button onClick={handleSMSHistory} style={dashboardButtonStyle}>SMS History</button>
        <button onClick={handlePurchaseCredit} style={billingButtonStyle}>Purchase Credits</button>
        <button onClick={handleRedeemCode} style={billingButtonStyle}>Redeem Code</button>
        <button onClick={handleLogout} style={logoutButtonStyle}>Logout</button>
      </div>
      <div style={dashboardStyle}>
        <div style={headerStyle}>
          <h1>Welcome, {currentUser.displayName}</h1>
          <p>
            API Key: {showApiKey ? apiKey : '••••••••••••••••••••••••••••••••••••••••••••••••'} 
            <button onClick={toggleApiKeyVisibility} style={iconButtonStyle}>
              {showApiKey ? (
                <img src="/eye-icon-open.png" alt="Hide" style={{ width: '20px', height: '20px' }} />
              ) : (
                <img src="/eye-icon-closed.png" alt="Show" style={{ width: '20px', height: '20px' }} />
              )}
            </button>
            <img 
              src="/copy-icon.png" 
              alt="Copy" 
              style={{ width: '20px', height: '20px', cursor: 'pointer' }} 
              onClick={copyApiKey} // Add click event to copy API key
            />
          </p>
        </div>
        {showPurchase ? (
          <div style={billingContainerStyle}>
            <h2>Purchase Credits</h2>
            <div style={regionLabelStyle}><a href="https://smstextr.web.app/regions" target="_blank" rel="noopener noreferrer">SMS Region:</a> USA, Canada, India, Germany, UK, Thailand, Australia, Spain, Portugal, Italy, Denmark, Ireland, Luxembourg and Poland</div>
            <div style={pricingBoxesStyle}>
              <div style={pricingBoxStyle}>
                <h3>Starter</h3>
                <p style={priceStyle}>$3</p>
                <div style={creditPillStyle}>50 SMS Credits</div>
                <button onClick={() => handlePurchase('price_1QIbosI7g6jAMftISmq7lwh4', 50)} style={purchaseButtonStyle}>Purchase</button>
              </div>
              <div style={pricingBoxStyle}>
                <h3>Basic</h3>
                <p style={priceStyle}>$10</p>
                <div style={creditPillStyle}>250 SMS Credits</div>
                <button onClick={() => handlePurchase('price_1QKmK0I7g6jAMftIK4RVwGz6', 250)} style={purchaseButtonStyle}>Purchase</button>
              </div>
              <div style={pricingBoxStyle}>
                <h3>Pro</h3>
                <p style={priceStyle}>$30</p>
                <div style={creditPillStyle}>1000 SMS Credits</div>
                <button onClick={() => handlePurchase('price_1QKmKQI7g6jAMftINS4qULbX', 1000)} style={purchaseButtonStyle}>Purchase</button>
              </div>
              <div style={pricingBoxStyle}>
                <h3>Enterprise</h3>
                <p style={priceStyle}>$50</p>
                <div style={creditPillStyle}>2500 SMS Credits</div>
                <button onClick={() => handlePurchase('price_1QIbrWI7g6jAMftIs0mnmx3c', 2500)} style={purchaseButtonStyle}>Purchase</button>
              </div>
            </div>
          </div>
        ) : dashboard ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}> {/* Added flex column layout */}
          <div style={pricingBoxesStyle}>
            <div style={pricingBoxStyle}>
              <h3>Total Credits Purchased</h3>
              <p style={priceStyle}>{credits.purchased}</p> 
            </div>
            <div style={pricingBoxStyle}>
              <h3>Total Credits Used</h3>
              <p style={priceStyle}>{credits.used}</p>  
            </div>
            <div style={pricingBoxStyle}>
              <h3>Total Credits Balance</h3>
              <p style={priceStyle}>{credits.balance}</p> 
            </div>      
            </div>
            <div style={{  color: 'green', marginLeft: '20px', marginTop: '30px', justifyContent: 'center', alignItems: 'center' }}>
              <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Webhook URL to Receive SMS:</label>
              <input 
                type="text" 
                value={webhookUrl} 
                onChange={(e) => setWebhookUrl(e.target.value)} 
                style={{ marginRight: '10px', color: 'Black', width: '400px' }} // Greyed out text
                placeholder="https://default-webhook-url.com" // Placeholder for the input
                //disabled={currentUser.uid !== 'QBbLAKnQc6Sbt3qCtab4o7tAVcH3' && currentUser.uid !== 'umFt72Y3VySCjZs00r1XAmOpf7m2' && currentUser.uid !== '020UrA0fJ6WeceRxE4a7cSk9imL2' && currentUser.uid !== 'zwQvcnG7BAhhkQDttFwUrfajxAu2'} // Disable the input field
              />
              <button 
                onClick={async () => {
                  try {
                    const userRef = ref(database, 'Users/' + currentUser.uid); // Reference to the user's data
                    await update(userRef, { WebhookUrl: webhookUrl }); // Update the WebhookUrl in the Users table
                    console.log('Webhook URL updated to:', webhookUrl);
                    alert('Webhook URL updated successfully!');
                  } catch (error) {
                    console.error('Error updating webhook URL:', error); // Log any errors
                  }
                }} 
                style={{ padding: '5px 10px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px' }} //backgroundColor: '#4CAF50'
                //disabled={currentUser.uid !== 'QBbLAKnQc6Sbt3qCtab4o7tAVcH3' && currentUser.uid !== 'umFt72Y3VySCjZs00r1XAmOpf7m2' && currentUser.uid !== '020UrA0fJ6WeceRxE4a7cSk9imL2' && currentUser.uid !== 'zwQvcnG7BAhhkQDttFwUrfajxAu2'} // Disable the button
              >
                Update
              </button>
            </div>        
                <p
                  style={{    color: 'green',
                    textAlign: 'right',
                    marginTop: 'auto',
                    position: 'absolute',
                    bottom: '20px',
                    left: '-20%', // Adjust as needed
                    width: '100%',
                  }}
                >
                For more information and to check out code snippets, visit our <a href="https://smstextr.web.app/docs" target="_blank" rel="noopener noreferrer">Documentation Page</a>.
              </p>
          </div>
          
        ) : redeemCode ? (
          <div style={{ backgroundColor: '#f0f0f0', padding: '20px', borderRadius: '10px' }}>
            <h2 style={{ color: '#4CAF50' }}>Redeem Your Code</h2>
            <label style={{ marginRight: '10px' }}>Enter your code to redeem credits:</label>
            <input 
              type="text" 
              placeholder="Enter code here" 
              style={{ marginRight: '10px' }} 
              value={redeemCodeValue} // Add state for input value
              onChange={(e) => setRedeemCodeValue(e.target.value)} // Update state on change
            />
            <button 
              onClick={() => {
                handleRedeem(redeemCodeValue);
              }} 
              style={{ 
                padding: '5px 10px', 
                backgroundColor: '#4CAF50', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: isLoading ? 'wait' : 'pointer' // Change cursor based on loading state
              }}
            >
              {isLoading ? 'Loading...' : 'Redeem'} {/* Change button text based on loading state */}
            </button>
            {error && <p style={{ color: 'red' }}>{error}</p>} {/* Display error message */}
          </div>
        ) : billingHistory ? (
          <div style={{ backgroundColor: '#f0f0f0', padding: '20px', borderRadius: '10px' }}>
            <h2 style={{ color: '#4CAF50' }}>Billing History</h2>
            {billingRecords.length > 0 ? (
              <>
                <table style={{ ...tableStyle, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: '#4CAF50', color: 'white' }}>Date</th>
                      <th style={{ backgroundColor: '#4CAF50', color: 'white' }}>Amount</th>
                      <th style={{ backgroundColor: '#4CAF50', color: 'white' }}>Credits Purchased</th>
                      <th style={{ backgroundColor: '#4CAF50', color: 'white' }}>Payment Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingRecords.slice(currentPage * recordsPerPage, (currentPage + 1) * recordsPerPage).map((record) => (
                      <tr key={record.id} style={{ borderBottom: '1px solid #ddd' }}>
                        <td>{record.date}</td>
                        <td>${record.amount}</td>
                        <td>{record.creditsPurchased}</td>
                        <td>{record.paymentMethod}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                  <button onClick={handlePreviousPage} disabled={currentPage === 0} style={paginationButtonStyle}>Previous</button>
                  <button onClick={handleNextPage} disabled={(currentPage + 1) * recordsPerPage >= billingRecords.length} style={paginationButtonStyle}>Next</button>
                </div>
              </>
            ) : (
              <div>
                <p>No billing history available.</p>
              </div>
            )}
          </div>
        ) : smsHistory ? (
          <div style={{ backgroundColor: '#f0f0f0', padding: '20px', borderRadius: '10px' }}>
            <h3 style={{ color: '#4CAF50' }}>SMS History</h3>
            <div style={{ marginBottom: '20px' }}>
              <button 
                onClick={() => setActiveTab('sent')} 
                style={{
                  ...tabButtonStyle,
                  backgroundColor: activeTab === 'sent' ? '#4CAF50' : '#e0e0e0',
                  color: activeTab === 'sent' ? 'white' : 'black',
                  marginRight: '10px'
                }}
              >
                SMS Sent
              </button>
              <button 
                onClick={() => setActiveTab('received')} 
                style={{
                  ...tabButtonStyle,
                  backgroundColor: activeTab === 'received' ? '#4CAF50' : '#e0e0e0',
                  color: activeTab === 'received' ? 'white' : 'black'
                }}
              >
                SMS Received
              </button>
            </div>
            {activeTab === 'sent' ? (
              smsRecords.length > 0 ? (
                <>
                  <table style={{ ...tableStyle, borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ backgroundColor: '#4CAF50', color: 'white' }}>Date</th>
                        <th style={{ backgroundColor: '#4CAF50', color: 'white' }}>Sent To</th>
                        <th style={{ backgroundColor: '#4CAF50', color: 'white' }}>Message</th>
                        <th style={{ backgroundColor: '#4CAF50', color: 'white' }}>Segments Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {smsRecords.slice(currentPage * recordsPerPage, (currentPage + 1) * recordsPerPage).map((record) => (
                        <tr key={record.id} style={{ borderBottom: '1px solid #ddd' }}>
                          <td style={{ textAlign: 'left', borderRight: '1px solid #ddd', minWidth: '150px', whiteSpace: 'nowrap' }}>{record.DateTime}</td>
                          <td style={{ textAlign: 'left', borderRight: '1px solid #ddd', minWidth: '150px', whiteSpace: 'nowrap' }}>{record.SentNumber}</td>
                          <td style={{ textAlign: 'left', borderRight: '1px solid #ddd', minWidth: '200px', whiteSpace: 'nowrap' }}>
                            {record.SentMessage.length > 30 ? (
                              <>
                                {record.SentMessage.substring(0, 30)}...
                                <button onClick={() => handleShowFullMessage(`Msg Id - ${record.MessageId} ${record.SentMessage}`)} 
                                style={{ marginLeft: '5px', background: 'none', border: 'none', color: '#4CAF50', cursor: 'pointer' }}>+</button>
                              </>
                            ) : (
                              record.SentMessage
                            )}
                          </td>
                          <td style={{ textAlign: 'right', minWidth: '100px', whiteSpace: 'nowrap' }}>{record.Segments}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '5px' }}>
                    <button onClick={handlePreviousPage} disabled={currentPage === 0} style={paginationButtonStyle}>Previous</button>
                    <button onClick={handleNextPage} disabled={(currentPage + 1) * recordsPerPage >= smsRecords.length} style={paginationButtonStyle}>Next</button>
                  </div>
                </>
              ) : (
                <div>
                  <p>No sent SMS history available.</p>
                </div>
              )
            ) : (
              smsReceivedRecords.length > 0 ? (
                <>
                  <table style={{ ...tableStyle, borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ backgroundColor: '#4CAF50', color: 'white' }}>Date</th>
                        <th style={{ backgroundColor: '#4CAF50', color: 'white' }}>Received From</th>
                        <th style={{ backgroundColor: '#4CAF50', color: 'white' }}>Message</th>
                        <th style={{ backgroundColor: '#4CAF50', color: 'white' }}>Segments Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {smsReceivedRecords.slice(currentPage * recordsPerPage, (currentPage + 1) * recordsPerPage).map((record) => (
                        <tr key={record.id} style={{ borderBottom: '1px solid #ddd' }}>
                          <td style={{ textAlign: 'left', borderRight: '1px solid #ddd', minWidth: '150px', whiteSpace: 'nowrap' }}>{record.DateTime}</td>
                          <td style={{ textAlign: 'left', borderRight: '1px solid #ddd', minWidth: '150px', whiteSpace: 'nowrap' }}>{record.Sender}</td>
                          <td style={{ textAlign: 'left', borderRight: '1px solid #ddd', minWidth: '200px', whiteSpace: 'nowrap' }}>
                            {record.Message.length > 30 ? (
                              <>
                                {record.Message.substring(0, 30)}...
                                <button onClick={() => handleShowFullMessage(`Msg Id - ${record.MessageId} ${record.Message}`)} 
                                style={{ marginLeft: '5px', background: 'none', border: 'none', color: '#4CAF50', cursor: 'pointer' }}>+</button>
                              </>
                            ) : (
                              record.Message
                            )}
                          </td>
                          <td style={{ textAlign: 'right', minWidth: '100px', whiteSpace: 'nowrap' }}>{record.Segments}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '5px' }}>
                    <button onClick={handlePreviousPage} disabled={currentPage === 0} style={paginationButtonStyle}>Previous</button>
                    <button onClick={handleNextPage} disabled={(currentPage + 1) * recordsPerPage >= smsReceivedRecords.length} style={paginationButtonStyle}>Next</button>
                  </div>
                </>
              ) : (
                <div>
                  <p>No received SMS history available.</p>
                </div>
              )
            )}
          </div>
        ) : (
          <div style={pricingBoxesStyle}>
            <div style={pricingBoxStyle}>
              <h3>Total Credits Purchased</h3>
              <p style={priceStyle}>{credits.purchased}</p>
            </div>
            <div style={pricingBoxStyle}>
              <h3>Total Credits Used</h3>
              <p style={priceStyle}>{credits.used}</p>
            </div>
            <div style={pricingBoxStyle}>
              <h3>Total Credits Balance</h3>
              <p style={priceStyle}>{credits.balance}</p>
            </div>
          </div>
        )}
        
        {transactionStatus && (
          <div style={popupStyle}>
            <div style={popupContentStyle}>
              <p>
                {transactionStatus === 'success'
                  ? `Your purchase of ${purchasedCreditsAmount} credits was successful!`
                  : 'Your purchase could not be completed. Please try again.'}
              </p>
              <button onClick={handleClosePopup} style={popupCloseButtonStyle}>
                Close
              </button>
            </div>
          </div>
        )}
        {isMessagePopupVisible && (
          <div style={popupStyle}>
            <div style={popupContentStyle}>
              <p>{fullMessage}</p>
              <button onClick={handleCloseMessagePopup} style={popupCloseButtonStyle}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const dashboardContainerStyle = {
  display: 'flex',
  minHeight: '100vh',
};

const sidebarStyle = {
  width: '200px',
  backgroundColor: '#222',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const logoStyle = {
  width: '100px',
  marginBottom: '30px',
};


const dashboardButtonStyle = {
  padding: '10px 20px',
  backgroundColor: '#4CAF50',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginBottom: '10px',
  width: '100%',
};

const billingButtonStyle = {
  padding: '10px 20px',
  backgroundColor: '#4CAF50',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginBottom: '10px',
  width: '100%',
};

const logoutButtonStyle = {
  padding: '10px 20px',
  backgroundColor: '#f44336',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginTop: 'auto',
  width: '100%',
};

const dashboardStyle = {
  flex: 1,
  padding: '20px',
  maxWidth: '800px',
  margin: '0 auto',
};

const headerStyle = {
  marginBottom: '30px',
  textAlign: 'center',
};

const billingContainerStyle = {
  marginTop: '50px',
  textAlign: 'center',
};

const pricingBoxesStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '20px',
};

const pricingBoxStyle = {
  flex: '1',
  padding: '20px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  margin: '0 10px',
  textAlign: 'center',
};

const purchaseButtonStyle = {
  padding: '10px 20px',
  backgroundColor: '#4CAF50',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginTop: '10px',
};

const priceStyle = {
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#4CAF50',
  margin: '10px 0',
};

const creditPillStyle = {
  backgroundColor: '#e0e0e0',
  borderRadius: '20px',
  padding: '5px 10px',
  display: 'inline-block',
  fontSize: '14px',
  marginBottom: '15px',
};

const popupStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const popupContentStyle = {
  backgroundColor: '#e0e0e0',   
  color: '#4CAF50', // Green text for the button
  padding: '20px',
  borderRadius: '8px',
  textAlign: 'center',
  width: '400px', // Set a specific width for the popup
  maxWidth: '90%', // Ensure it doesn't exceed 90% of the viewport width
};

const popupCloseButtonStyle = {
  padding: '10px 20px',
  backgroundColor: '#4CAF50', // White background for the button
  color: '#fff', // Green text for the button
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginTop: '10px',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '20px',
};

const thTdStyle = {
  border: '1px solid #ddd',
  padding: '8px',
  textAlign: 'left',
};

const regionLabelStyle = {
  color: 'green',          // Set the color to green
  fontWeight: 'bold',      // Make it bold for emphasis
  marginBottom: '10px',    // Add some space between the label and the pricing boxes
  textAlign: 'left',       // Align it to the left
};

const paginationButtonStyle = {
  padding: '5px 10px',
  backgroundColor: '#4CAF50',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  marginLeft: '5px',
};

const tabButtonStyle = {
  padding: '10px 20px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontWeight: 'bold',
  transition: 'all 0.3s ease',
};

// Add this to your existing style objects
Object.assign(tableStyle, {
  'th, td': thTdStyle,
  'th': {
    backgroundColor: '#f2f2f2',
    fontWeight: 'bold',
  },
  'tr:nth-child(even)': {
    backgroundColor: '#f9f9f9',
  },
});

export default DashboardPage;
